<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import api from "@/services/apiService";

import ArgonButton from "@/components/ArgonButton.vue";
import BackendPagination from "@/components/BackendPagination.vue";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import HouseDetails from "./components/HouseDetails.vue";
import noDataImg from "@/assets/img/illustrations/noData.png";

const imgUrl = process.env.VUE_APP_API;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const houses = ref([]);
const categories = ref([]);
const amenities = ref([]);
const errorMessage = ref("");
const loading = ref(true);
const router = useRouter();

const showModal = ref(false);
const showDetailsModal = ref(false);
const isEditing = ref(false);
const selectedHouse = ref(null);
const selectedAmenityObjects = ref([]);
const thumbnail = ref(null);
const images = ref([]);
const removedImages = ref([]);

// Pagination
const currentPage = ref(1);
const itemsPerPage = 10;
const totalItems = ref(0);
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage));
const provinces = ref([]);

// Fetch Houses
const fetchHouses = async () => {
    loading.value = true;
    try {
        const response = await api.get(`/houses?page=${currentPage.value}`);
        houses.value = response.data.member || [];
        totalItems.value = response.data.totalItems || 0;
    } catch (error) {
        errorMessage.value = error.response?.data?.message || "Error al obtener casas.";
    } finally {
        loading.value = false;
    }
};

// Fetch Selects
const fetchSelectOptions = async () => {
    try {
        const [provincesRes, categoriesRes, amenitiesRes] = await Promise.all([
            api.get("/provinces?pagination=false"),
            api.get("/categories?pagination=false"),
            api.get("/amenities?pagination=false")
        ]);

        provinces.value = provincesRes.data.member || [];
        categories.value = categoriesRes.data.member || [];
        amenities.value = amenitiesRes.data.member || [];

    } catch (error) {
        console.error("Error al cargar listas desplegables:", error);
    }
};

const amenityOptions = computed(() =>
    amenities.value.map((a) => ({ id: a.id, name: a.name }))
);

// Page Change
const changePage = async (page) => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        await fetchHouses();
    }
};

// View Details
const viewHouseDetails = (house) => {
    selectedHouse.value = house;
    showDetailsModal.value = true;
};

// Open Add/Edit Modal
const openModal = () => {
    isEditing.value = false;
    selectedHouse.value = {
        title: "",
        address: "",
        surface: null,
        numBedrooms: null,
        numBathrooms: null,
        price: null,
        status: "",
        category: "",
        city: "",
        amenities: [],
        images: [],
        thumbnail: ""
    };
    selectedAmenityObjects.value = [];
    thumbnail.value = null;
    images.value = [];
    removedImages.value = [];
    errorMessage.value = "";

    showModal.value = true;
    fetchSelectOptions();
};

const openEditModal = (house) => {
    isEditing.value = true;
    selectedHouse.value = { ...house };
    selectedAmenityObjects.value = house.amenities || [];
    selectedHouse.value.city = house.city?.id || "";
    selectedHouse.value.category = house.category?.id || "";
    thumbnail.value = null;
    images.value = [];
    removedImages.value = [];
    errorMessage.value = "";

    showModal.value = true;
    fetchSelectOptions();
};

const closeModal = () => {
    showModal.value = false;
    selectedHouse.value = null;
    thumbnail.value = null;
    images.value = [];
    removedImages.value = [];

    setTimeout(() => {
        errorMessage.value = "";
    }, 300);
};

const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > MAX_FILE_SIZE) {
        errorMessage.value = "La imagen miniatura supera el tamaño máximo de 5MB.";
        thumbnail.value = null;
        return;
    }
    errorMessage.value = "";
    thumbnail.value = file || null;
};

const handleImagesChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    const oversized = newFiles.find(file => file.size > MAX_FILE_SIZE);

    if (oversized) {
        errorMessage.value = "Una o más imágenes superan el tamaño máximo de 5MB.";
        return;
    }

    errorMessage.value = "";
    images.value = [...images.value, ...newFiles];
    e.target.value = null;
};

const removeExistingImage = async (img) => {
    if (!img.id) return;

    try {
        await api.delete(`/images/${img.id}`);
        selectedHouse.value.images = selectedHouse.value.images.filter(i => i.id !== img.id);
    } catch (err) {
        console.error("Error al eliminar imagen:", err);
    }
};

// Save House
const saveHouse = async () => {
    try {
        const formData = new FormData();
        selectedHouse.value.amenities = selectedAmenityObjects.value.map(a => a.id);

        for (const key in selectedHouse.value) {
            const val = selectedHouse.value[key];
            if (Array.isArray(val)) {
                val.forEach(item => formData.append(`${key}[]`, item));
            } else {
                formData.append(key, val);
            }
        }

        if (thumbnail.value) {
            formData.append("thumbnail", thumbnail.value);
        }

        if (images.value.length) {
            images.value.forEach(file => {
                formData.append("images[]", file);
            });
        }

        if (isEditing.value && removedImages.value.length) {
            removedImages.value.forEach(img => {
                formData.append("removedImages[]", img);
            });
        }

        const userData = JSON.parse(localStorage.getItem("userData"));
        if (!userData?.id) throw new Error("No valid user ID found in localStorage.");
        formData.append("user", userData.id);

        if (isEditing.value) {
            formData.append("_method", "PUT");
            const houseId = selectedHouse.value["@id"].replace(/^\/api/, "");
            await api.post(houseId, formData);
        } else {
            await api.post("/houses", formData);
        }

        await fetchHouses();
        closeModal();
    } catch (error) {
        if (error.response) {
            if (error.response.status === 413) {
                errorMessage.value = "El archivo enviado es demasiado grande. Por favor, reduce el tamaño de las imágenes.";
            } else {
                errorMessage.value = error.response.data?.message || "Error al guardar la casa.";
            }
        } else if (error.request) {
            // Request made but no response received
            errorMessage.value = "No se recibió respuesta del servidor. El archivo puede ser demasiado grande.";
        } else {
            // Something else happened
            errorMessage.value = "Error inesperado: " + error.message;
        }

        console.error("Full error info:", error);
    }

};

// Delete House
const deleteHouse = async (house) => {
    if (!confirm(`¿Eliminar la casa "${house.title}"?`)) return;
    try {
        const houseId = house["@id"].replace(/^\/api/, "");
        await api.delete(houseId);
        fetchHouses();
    } catch (error) {
        console.error("Error al eliminar casa:", error);
    }
};

// Label helper
const getStatusLabel = (status) => {
    const statusMap = {
        SELLE: "Vender",
        RENT: "Alquilar",
        NEW_PROPERTY: "Nueva Propiedad",
        VACATION_RENTAL: "Alquiler Vacacional"
    };
    return statusMap[status] || status;
};

onMounted(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
        router.push("/signin");
    } else {
        fetchHouses();
    }
});
</script>


<template>
    <div class="py-4 container-fluid">
        <div class="row">
            <div class="col-12">
                <div class="card p-3">
                    <div class="card-header pb-0 d-flex">
                        <h6>Casas</h6>
                        <argon-button color="primary" size="sm" class="ms-auto" @click="openModal">
                            <font-awesome-icon :icon="['fas', 'plus']" class="me-2" />
                            Agregar Casa
                        </argon-button>
                    </div>
                    <div class="card-body px-0 pt-0 pb-2">
                        <div class="table-responsive p-0">
                            <div v-if="loading" class="text-center py-3">
                                <p class="text-secondary">Cargando casas...</p>
                            </div>
                            <div v-if="houses.length === 0 && !loading && !errorMessage" class="text-center py-3">
                                <img :src="noDataImg" alt="No hay datos disponibles" width="300">
                                <p class="text-secondary">No se encontraron casas</p>
                            </div>

                            <table v-if="houses.length > 0" class="table text-center align-items-center mb-0 mt-3">
                                <thead>
                                    <tr>


                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                            image</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                            Título</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                            Ciudad</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                            Superficie</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                            Dormitorios</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                            Baños</th>
                                        <th
                                            class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ">
                                            Precio</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                            Estado</th>

                                        <th
                                            class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                                        </th>


                                    </tr>
                                </thead>
                                <tbody>
                                    <tr class="text-center" v-for="house in houses" :key="house['@id']">
                                        <!-- Thumbnail -->
                                        <td>

                                            <img v-if="house.thumbnail"
                                                :src="`${imgUrl}/uploads/thumbnails/${house.thumbnail}`" alt="Thumbnail"
                                                style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px;" />


                                            <img v-else src="../assets/img/no-image.svg" alt="Thumbnail"
                                                style="width: 80px; height: auto; object-fit: cover; border-radius: 8px;" />
                                        </td>

                                        <!-- Title -->
                                        <td>{{ house.title }}</td>


                                        <!-- City -->
                                        <td>{{ house.city?.name || 'Sin ciudad' }}</td>

                                        <!-- Surface -->
                                        <td>{{ house.surface }} m²</td>

                                        <!-- Bedrooms -->
                                        <td>{{ house.numBedrooms }}</td>

                                        <!-- Bathrooms -->
                                        <td>{{ house.numBathrooms }}</td>

                                        <!-- Price -->
                                        <td>${{ house.price }}</td>

                                        <!-- Status -->
                                        <td class="align-middle text-center text-sm">
                                            <span class="badge badge-sm bg-gradient-warning">{{
                                                getStatusLabel(house.status) }}</span>
                                        </td>



                                        <!-- Actions -->
                                        <td>
                                            <font-awesome-icon :icon="['fas', 'pen-to-square']" class="me-3"
                                                @click="openEditModal(house)" style="cursor:pointer;" />
                                            <font-awesome-icon :icon="['fas', 'trash']" @click="deleteHouse(house)"
                                                class="me-3" style="cursor:pointer;" />
                                            <font-awesome-icon :icon="['fas', 'eye']" class="me-3"
                                                @click="viewHouseDetails(house)" style="cursor:pointer;"
                                                title="Ver detalles" />


                                        </td>
                                    </tr>

                                </tbody>
                            </table>
                            <BackendPagination :current-page="currentPage" :total-pages="totalPages"
                                @change-page="changePage" />
                        </div>
                    </div>
                </div>



                <!-- Modal -->
                <teleport to="body">
                    <div v-if="showModal" class="modal-overlay" @click="closeModal">
                        <div class="modal-content overflow-auto" @click.stop style="max-height: 90vh">
                            <h5 class="mb-3">{{ isEditing ? "Editar Casa" : "Agregar Casa" }}</h5>

                            <div v-if="errorMessage" class="text-center text-danger py-3">
                                {{ errorMessage }}
                            </div>

                            <!-- Title + Address -->
                            <div class="row mb-3">
                                <div class="col">
                                    <label>Título</label>
                                    <input v-model="selectedHouse.title" class="form-control" type="text" required
                                        placeholder="Ingresa el título" />
                                </div>
                                <div class="col">
                                    <label>Dirección</label>
                                    <input v-model="selectedHouse.address" class="form-control" type="text" required
                                        placeholder="Ingresa la dirección" />
                                </div>
                            </div>

                            <!-- Surface + Bedrooms -->
                            <div class="row mb-3">
                                <div class="col">
                                    <label>Superficie</label>
                                    <input v-model="selectedHouse.surface" class="form-control" type="number" required
                                        placeholder="Ej: 120" />
                                </div>
                                <div class="col">
                                    <label>Dormitorios</label>
                                    <input v-model="selectedHouse.numBedrooms" class="form-control" type="number"
                                        required placeholder="Ej: 3" />
                                </div>
                            </div>

                            <!-- Bathrooms + Price -->
                            <div class="row mb-3">
                                <div class="col">
                                    <label>Baños</label>
                                    <input v-model="selectedHouse.numBathrooms" class="form-control" type="number"
                                        required placeholder="Ej: 2" />
                                </div>
                                <div class="col">
                                    <label>Precio</label>
                                    <input v-model="selectedHouse.price" class="form-control" type="number" required
                                        placeholder="Ej: 150000" />
                                </div>
                            </div>

                            <!-- Estado + Categoría -->
                            <div class="row mb-3">
                                <div class="col">
                                    <label>Estado</label>
                                    <select v-model="selectedHouse.status" class="form-control">
                                        <option disabled value="">Selecciona un estado</option>
                                        <option value="SELLE">Vender</option>
                                        <option value="RENT">Alquilar</option>
                                        <option value="NEW_PROPERTY">Alquiler Vacacional</option>
                                        <option value="VACATION_RENTAL">Nueva Propiedad</option>
                                    </select>
                                </div>
                                <div class="col">
                                    <label>Categoría</label>
                                    <select v-model="selectedHouse.category" class="form-control">
                                        <option disabled value="">Selecciona una categoría</option>
                                        <option v-for="cat in categories" :key="cat['id']" :value="cat['id']">
                                            {{ cat.type }}
                                        </option>
                                    </select>
                                </div>
                            </div>

                            <!-- Ciudad + Amenidades -->
                            <div class="row mb-3">
                                <div class="col">
                                    <label>Ciudad</label>
                                    <select v-model="selectedHouse.city" class="form-control">
                                        <option disabled value="">Selecciona una ciudad</option>
                                        <optgroup v-for="province in provinces" :label="province.name"
                                            :key="province.id">
                                            <option v-for="city in province.cities" :key="city.id" :value="city.id">
                                                {{ selectedHouse.city === city.id ? city.name : `${city.name}
                                                (${city.houseCount})` }}
                                            </option>


                                        </optgroup>
                                    </select>
                                </div>

                                <div class="col">
                                    <label>Amenidades</label>
                                    <Multiselect v-model="selectedAmenityObjects" :options="amenityOptions"
                                        :multiple="true" :close-on-select="false" placeholder="Selecciona amenidades"
                                        label="name" track-by="id" />
                                </div>
                            </div>

                            <!-- Thumbnail + Multiple Images -->
                            <div class="row mb-3">
                                <div class="col">
                                    <label>Imagen Miniatura</label>
                                    <input type="file" class="form-control" accept="image/*"
                                        @change="handleThumbnailChange" />
                                    <div v-if="isEditing && selectedHouse.thumbnail" class="text-muted small mt-1">
                                        {{ selectedHouse.thumbnail }}
                                    </div>
                                </div>
                                <div class="col">
                                    <label class="form-label">Otras Imágenes</label>
                                    <input type="file" class="form-control" accept="image/*" multiple
                                        @change="handleImagesChange" />

                                    <!-- Existing images (for edit mode) -->
                                    <div v-if="isEditing && selectedHouse.images?.length" class="mt-2">
                                        <label class="form-label mt-2">Imágenes existentes</label>
                                        <div v-for="(img, index) in selectedHouse.images" :key="'existing-' + index"
                                            class="d-flex align-items-center justify-content-between mb-1">
                                            <small>{{ img.imageName }}</small>
                                            <font-awesome-icon :icon="['fas', 'trash']" class="text-danger"
                                                style="cursor:pointer;" @click="removeExistingImage(img)" />
                                        </div>
                                    </div>

                                    <!-- Newly selected images (not yet uploaded) -->
                                    <div v-if="images.length" class="mt-3">
                                        <label class="form-label">Nuevas imágenes seleccionadas</label>
                                        <div v-for="(file, index) in images" :key="'new-' + index"
                                            class="d-flex align-items-center justify-content-between mb-1">
                                            <small>{{ file.name }}</small>
                                            <font-awesome-icon :icon="['fas', 'trash']" class="text-danger"
                                                style="cursor:pointer;" @click="images.splice(index, 1)" />
                                        </div>
                                    </div>
                                </div>

                            </div>


                            <!-- Buttons -->
                            <div class="d-flex justify-content-end">
                                <argon-button color="secondary" class="me-2" @click="closeModal">Cancelar</argon-button>
                                <argon-button color="primary" @click="saveHouse">
                                    {{ isEditing ? "Guardar Cambios" : "Agregar" }}
                                </argon-button>
                            </div>
                        </div>
                    </div>
                </teleport>
                <teleport to="body">
                    <HouseDetails v-if="showDetailsModal" :house="selectedHouse" @close="showDetailsModal = false" />
                </teleport>




            </div>
        </div>

    </div>
</template>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
}

.modal-content {
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.3);
    z-index: 1051;
    width: 600px;
    max-width: 90%;
    position: relative;
}

::v-deep(.multiselect__tag) {
    background-color: #facc15 !important;
    /* Yellow */

}
</style>